import { makeAssistantToolUI } from '@assistant-ui/react'
import { Table2 } from 'lucide-react'
import { useParams } from 'react-router-dom'
import { ToolCallCard, ToolCallHeader } from '@/components/agent/tools/_shared/ToolCallHeader'
import { CaseEvidence } from '@/features/evidence/CaseEvidence'
import { parseSourceToolResult, type SourceToolResult } from '@/features/source-snippets/result'
import { SourceSnippetResult } from '@/features/source-snippets/SourceSnippetResult'
import { useToolCardOpen } from './useToolCardOpen'

function StructuredEvidenceRender({
  result,
  artifact,
  status,
}: {
  result?: SourceToolResult | string
  artifact?: { source_result?: SourceToolResult }
  status: { type: string }
}) {
  const parsed = parseSourceToolResult(artifact?.source_result ?? result)
  const entries = parsed?.results ?? (parsed ? [parsed] : [])
  const running = status.type === 'running'
  const error = status.type === 'incomplete' || Boolean(parsed?.error)
  const { open, onToggle } = useToolCardOpen(!running && Boolean(parsed), false, error)
  const count = entries.reduce(
    (sum, entry) => sum + (entry.snippet?.snapshot.result.rows.length ?? entry.count ?? 0),
    0
  )
  return (
    <ToolCallCard>
      <ToolCallHeader
        status={running ? 'running' : error ? 'error' : 'complete'}
        icon={Table2}
        toolName="Structured source"
        description={running ? 'Searching source rows' : `${count} rows`}
        expandable={!running && Boolean(parsed)}
        open={open}
        onToggle={onToggle}
      />
      {open && (
        <div className="min-w-0 space-y-3 border-t border-border px-3 py-2">
          {parsed?.error && <p className="text-xs text-destructive">{parsed.error}</p>}
          {parsed?.coverage?.complete === false && (
            <p className="text-xs text-muted-foreground">Some sources could not be searched.</p>
          )}
          {!error && !entries.length && <p className="text-xs">No result rows available.</p>}
          {entries.map((entry, index) => (
            <SourceResultEntry key={entry.evidence_reference_id ?? index} entry={entry} />
          ))}
        </div>
      )}
    </ToolCallCard>
  )
}

function SourceResultEntry({ entry }: { entry: SourceToolResult }) {
  const { caseId } = useParams<{ caseId: string }>()
  if (entry.snippet) return <SourceSnippetResult snippet={entry.snippet} />
  // Older conversations contain the rows/reference but predate signed artifacts.
  if (caseId && entry.evidence_reference_id)
    return <CaseEvidence caseId={caseId} referenceId={entry.evidence_reference_id} />
  return null
}

function sourceTool(toolName: string) {
  return makeAssistantToolUI<Record<string, unknown>, SourceToolResult | string>({
    toolName,
    render: StructuredEvidenceRender,
  })
}

export const QueryStructuredTableToolUI = sourceTool('query_structured_table')
export const SearchStructuredEvidenceToolUI = sourceTool('search_structured_evidence')
export const CiteStructuredRowsToolUI = sourceTool('cite_structured_rows')
export const OpenCaseEvidenceToolUI = sourceTool('open_case_evidence')
